import './thesaurus-term-tree-modal.scss'

import _ from 'lodash'
import { Subject, of, zip } from 'rxjs'
import { debounceTime, distinctUntilChanged, first, map, mergeMap, switchMap } from 'rxjs/operators'
import Component from "vue-class-component"
import { Watch } from 'vue-property-decorator'
import { BModal } from 'bootstrap-vue'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'

import { ThesaurusTerm, ThesaurusTermTreeNode, getTranslatedString } from '@skinsoft/types'
import { thesaurusTermSelectors } from '@skinsoft/selectors'
import { ThesaurusDataStore, ThesaurusTermDataStore } from '@skinsoft/entity'

import { ReactiveVue } from "@skinsoft/reactive-vue"
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus/src/busTopics'
import ThesaurusTreeNode, { THESAURUS_TREE_NODE_OPENED, THESAURUS_TREE_NODE_SELECTED, ThesaurusTreeNodeSelected } from '../thesaurus-tree-node'
import ThesaurusTermCreateForm, { THESAURUS_TERM_CREATED } from '../thesaurus-term-create-form'
import { SELECTED_TAB_CHANGE_EVENT, TabsMenu } from '../../tabs-menu'

export const THESAURUS_TERM_SELECTED = 'thesaurus-term-selected'

@Component({
    components: {
        sThesaurusTreeNode: ThesaurusTreeNode,
        sThesaurusTermCreateForm: ThesaurusTermCreateForm,
        sTabsMenu: TabsMenu,
    },
    props: {
        types: Array,
        thesaurusIdentifier: {
            type: String,
        },
        startValue: {
            type: String,
        },
    },
    template: require('./thesaurus-term-tree-modal.html')
})
export default class ThesaurusTermTreeModal extends ReactiveVue {
    private thesaurusIdentifier: string
    private startValue: string

    private thesaurusId: string = ""
    private startThesaurusTerm: ThesaurusTerm
    private thesaurusTree: ThesaurusTermTreeNode[] = []
    private selectedThesaurusTermId: string = ""
    private filterText: string = ""
    private isLoading: boolean = true

    private $thesaurusDataStore: ThesaurusDataStore
    private $thesaurusTermDataStore: ThesaurusTermDataStore

    private loadingIcon = faCircleNotch

    private filterText$ = new Subject<string>()
    private filterTextChange$ = this.filterText$.pipe(
        distinctUntilChanged(),
        debounceTime(300),
    )

    public $refs: {
        modal: BModal
    }

    private tabsTitle: string[] = []
    private selectedTabIndex: number = 0

    private THESAURUS_TREE_NODE_SELECTED = THESAURUS_TREE_NODE_SELECTED
    private THESAURUS_TREE_NODE_OPENED = THESAURUS_TREE_NODE_OPENED
    private THESAURUS_TERM_CREATED = THESAURUS_TERM_CREATED
    private SELECTED_TAB_CHANGE_EVENT = SELECTED_TAB_CHANGE_EVENT

    private mounted() {
        this.$thesaurusDataStore.fetchOneByIdentifier(this.thesaurusIdentifier)
            .subscribe(thesaurus => this.thesaurusId = thesaurus.id)

        this.tabsTitle = [
            this.$t('thesaurusTermTree.selectionTabTitle') as string,
            this.$t('thesaurusTermTree.creationTabTitle') as string,
        ]
        this.selectedThesaurusTermId = this.startValue
        this.getThesaurusTerms()
        this.subscribeTo(this.filterTextChange$, filterText => {
            this.getThesaurusTerms()
        })
    }

    private getThesaurusTerms() {
        this.isLoading = true
        if(this.filterText) {
            this.searchThesaurusTerms()
        } else {
            this.fetchThesaurusTerms()
        }
    }

    private searchThesaurusTerms() {
        this.$thesaurusDataStore.fetchOne(this.thesaurusId)
            .pipe(
                switchMap(thesaurus => {
                    return this.$thesaurusTermDataStore.searchOnRoot(thesaurus.id, this.$i18n.locale, this.filterText, true)
                }),
                map(response => {
                    return response.data ?? response.allData
                }),
                switchMap(thesaurusTerms => {
                    /* The searchOnRoot request returns ThesaurusTerms that match the filterText. But we want to display the matching Terms within their parents
                     * but the parents are not necessarily returned by the request. So we need to add the missing parents to the tree structure
                     * The strategy here is to use the pathIds property of thesaurus terms fetched to build the tree structure including the missing parents
                     *
                     * For each thesaurus term of the request, we eventually build the tree structure from its parents to itself, then we add the root in res.
                     * The reconstructed parents, contains only the id.
                     * When we build a node structure, we look if the nodes (including the node itself) already exists in the res tree (using the id). If they
                     * already exist, we simply add the next node inside the one that already exist and we continue building the tree. If the node that we want
                     * to add is the last one (the one we have the data), we update the existing node with the data.
                     *
                     * Additionnaly, we keep track of the node that we created and we don't have data. And after having build the tree, we send a request to get
                     * the data of the node we miss, and update our tree with the results.
                    */
                    let res: ThesaurusTermTreeNode[] = []
                    let termsToFetchIds: string[] = []
                    let nodesToUpdate: ThesaurusTermTreeNode[] = []

                    for(const thesaurusTerm of thesaurusTerms) {
                        let node = {
                            childrenTerms: [] as ThesaurusTermTreeNode[],
                            term: thesaurusTerm,
                        }

                        let thesaurusPathsIds: string = thesaurusTermSelectors.pathIds(thesaurusTerm)
                        if(thesaurusPathsIds && thesaurusPathsIds.charAt(0) === '/') {
                            thesaurusPathsIds = thesaurusPathsIds.slice(1)
                        }
                        // the last index of parentsIdsInOrder is the term id itself
                        const parentsIdsInOrder: string[] = thesaurusPathsIds.split('/')
                        const nodeLevel = parentsIdsInOrder.length

                        if(parentsIdsInOrder) {
                            let root = res.find(node => node.term.id === parentsIdsInOrder[0])
                            if(root) {
                                if(nodeLevel === 1) {
                                    _.merge(root.term, thesaurusTerm)
                                    _.remove(termsToFetchIds, termToFetchId => termToFetchId ===  (root as ThesaurusTermTreeNode).term.id)
                                    _.remove(nodesToUpdate, nodeToUpdate => nodeToUpdate.term.id === (root as ThesaurusTermTreeNode).term.id)
                                    continue
                                }
                            } else {
                                root = {
                                    childrenTerms: [] as ThesaurusTermTreeNode[],
                                    term: { id: parentsIdsInOrder[0] },
                                }
                                if(nodeLevel === 1) {
                                    root = {
                                        childrenTerms: [] as ThesaurusTermTreeNode[],
                                        term: thesaurusTerm,
                                    }
                                    res.push(root)
                                    continue
                                } else {
                                    termsToFetchIds.push(parentsIdsInOrder[0])
                                    nodesToUpdate.push(root)
                                }
                                res.push(root)
                            }

                            let currentNode: ThesaurusTermTreeNode = root as ThesaurusTermTreeNode

                            // building the tree structure
                            for(let level=1; level < nodeLevel-1; level++) {
                                let subNode = currentNode.childrenTerms.find(node => node.term.id === parentsIdsInOrder[level])

                                if(!subNode) {
                                    subNode = {
                                        childrenTerms: [] as ThesaurusTermTreeNode[],
                                        term: { id: parentsIdsInOrder[level] },
                                    }

                                    currentNode.childrenTerms.push(subNode)
                                    nodesToUpdate.push(subNode)
                                    termsToFetchIds.push(parentsIdsInOrder[level])
                                }

                                // @ts-ignore
                                currentNode = subNode
                            }

                            // adding the thesaurus term itself to the tree
                            let finalNode = currentNode.childrenTerms.find(node => node.term.id === thesaurusTerm.id)
                            if(finalNode) {
                                _.merge(finalNode.term, thesaurusTerm)
                                _.remove(termsToFetchIds, termToFetchId => termToFetchId ===  (finalNode as ThesaurusTermTreeNode).term.id)
                                _.remove(nodesToUpdate, nodeToUpdate => nodeToUpdate.term.id === (finalNode as ThesaurusTermTreeNode).term.id)
                            } else {
                                // adding node corresponding to thesaurusTerm to direct parent
                                currentNode.childrenTerms.push(node)
                            }
                        }
                    }

                    // fetch all missing ids
                    const missingIds$ = this.$thesaurusTermDataStore.fetchMany2(termsToFetchIds, this.$i18n.locale)
                    return zip(missingIds$, of(nodesToUpdate), of(res))
                }),
                map(([response, nodesToUpdate, res]) => {
                    const additionnalThesaurusTerms: ThesaurusTerm[] = response.data ?? response.allData ?? []
                    return [additionnalThesaurusTerms, nodesToUpdate, res]
                })
            )
            .subscribe(
                ([additionnalThesaurusTerms, nodesToUpdate, res]) => {
                    const nodesToUpdateWithType = nodesToUpdate as ThesaurusTermTreeNode[]

                    // merging fetched ThesaurusTerms to res
                    for(let node of nodesToUpdateWithType) {
                        const termUpdateIndex = additionnalThesaurusTerms.findIndex((term) => term.id === node.term.id)
                        if(termUpdateIndex >= 0) {
                            _.merge(node.term, additionnalThesaurusTerms[termUpdateIndex])
                            additionnalThesaurusTerms.splice(termUpdateIndex, 1)
                        }
                    }

                    this.thesaurusTree = res as ThesaurusTermTreeNode[]
                    this.sortTree()
                    this.isLoading = false
                },
                (err) => {
                    this.isLoading = false
                    const alertPayload = {
                        message: this.$t('thesaurusTermTree.errors.searchError'),
                        variant: "danger"
                    };
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                }
            )
    }

    private fetchThesaurusTerms() {
        this.$thesaurusDataStore.fetchOneByIdentifier(this.thesaurusIdentifier)
            .pipe(
                mergeMap(thesaurus => {
                    return this.$thesaurusTermDataStore.searchOnRoot(thesaurus.id, this.$i18n.locale, '', false);
                }),
                map(response => {
                    return response.data ?? response.allData ?? []
                })
            ).subscribe(
                async terms => {
                    this.thesaurusTree = []
                    this.thesaurusTree.push(...terms.map(term => {
                        return {
                            term: term,
                            childrenTerms: [],
                        } as ThesaurusTermTreeNode
                    }))

                    for(let parentTerm of this.thesaurusTree) {
                        this.fetchChildren(parentTerm)
                    }
                    this.sortTree()
                    this.isLoading = false
                },
                (error) => {
                    this.isLoading = false
                    const alertPayload = {
                        message: this.$t('thesaurusTermTree.errors.fetchError'),
                        variant: "danger"
                    };
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                }
            )
    }

    private async fetchChildren(parentTerm: ThesaurusTermTreeNode) {
        parentTerm.childrenTerms = []
        const children = await this.$thesaurusTermDataStore.fetchChildren(parentTerm.term.id, this.$i18n.locale).pipe(first()).toPromise()

        if(children && children.length) {
            parentTerm.childrenTerms.push(
                ...children.map(thesaurusTerm => {
                    return {
                        term: thesaurusTerm,
                        childrenTerms: [],
                    } as ThesaurusTermTreeNode
                })
                ?.sort(this.thesaurusNodeSortFunction)
            )
        }
    }

    private sortTree() {
        this.thesaurusTree.sort(this.thesaurusNodeSortFunction)
    }

    private thesaurusNodeSortFunction(termA: ThesaurusTermTreeNode, termB: ThesaurusTermTreeNode): number {
        const termAValue: string = getTranslatedString(thesaurusTermSelectors.preferredTerm(termA.term), this.$i18n.locale)
        const termBValue: string = getTranslatedString(thesaurusTermSelectors.preferredTerm(termB.term), this.$i18n.locale)
    
        if(!termA) {
            if(!termB) {
                return 0
            }
            return -1
        }
        if(!termB) {
            return 1
        }
    
        let res = termAValue.localeCompare(termBValue)
        if(res === 0) {
            if(termA.childrenTerms?.length) {
                if(termB.childrenTerms?.length) {
                    // Both terms have children
                    return 0
                } else {
                    // A has children but not B
                    return -1
                }
            }
    
            if(termB.childrenTerms?.length) {
                // B has children but A has not
                return 1
            }
        }
    
        return res
    }

    private onThesaurusTermSelected(event: ThesaurusTreeNodeSelected) {
        this.selectedThesaurusTermId = event.selectedThesaurusTermId
    }

    private onThesaurusTermOpened(event: ThesaurusTermTreeNode) {
        if(!this.filterText) {
            for(let child of event.childrenTerms) {
                this.fetchChildren(child)
            }
        }
    }

    private onValidateSelection() {
        this.$emit(THESAURUS_TERM_SELECTED, this.selectedThesaurusTermId)
        this.$refs.modal.hide()
    }

    public openModal() {
        this.$refs.modal.show()
    }

    private onSelectedTabChanged(selectedTab: number) {
        this.selectedTabIndex = selectedTab
    }

    @Watch('startValue')
    private onStartValueChanged() {
        this.$thesaurusTermDataStore.fetchOne(this.startValue).subscribe(thesaurusTerm => {
            if(thesaurusTerm) {
                this.startThesaurusTerm = thesaurusTerm
            }
        })
    }

    @Watch('filterText')
    private onFilterTextChange() {
        this.filterText$.next(this.filterText)
    }
}